import { computed, effect, inject, Injectable, ResourceRef, Signal, signal, WritableSignal } from "@angular/core";
import { rxResource, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ILatLng } from "polydraw";
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, of, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { ImagePackage } from "src/models/common/image.model";
import { IResidenceSaleInArea, SaleInAreaParams } from "src/models/estate/IResidenceSaleInArea";
import { ISalePositionInArea } from "src/models/estate/ISalePositionsInArea";
import { MiniInfocardEstateSale } from "src/models/estate/mini-infocard-estate.model";
import { MapBoundsState } from "src/models/map/map-bounds-state.model";
import { MapMarkerType } from "src/models/map/map-marker-type.enum";
import { AreasStore } from "src/stores/area-builder.store";
import { HttpDataService, IResponseWrapper } from "../httpdata.service";
import { MapActionsService } from "../map/map-actions.service";
import { MapMode, MapStateService } from "../map/map-state.service";
import { ResourceService } from "../resources/resource.service";
import { MarketSalesStateService } from "./market-sales-state.service";
import { EVEstateType } from "src/models/units/unit-attributes.model";

@Injectable({
    providedIn: "root",
})
export class MarketSalesDataService {
    private readonly http = inject(HttpDataService);
    private readonly mapStateService = inject(MapStateService);
    private readonly marketSalesStateService = inject(MarketSalesStateService);
    private readonly mapActionsService = inject(MapActionsService);
    private readonly resourceService = inject(ResourceService);
    private readonly areasStore = inject(AreasStore);

    private minSalesZoom = 12;
    private initHasRun: boolean = false;

    selectedSale: WritableSignal<SaleInAreaParams | null> = signal(null);
    mapMode = this.mapStateService.mapModeSignal;
    url = `${environment.apiUrl}/Sales/ResidenceSalesInArea`;
    salePositionsurl = `${environment.apiUrl}/Map/SalePositionsInArea`;
    targetEstateSale: MiniInfocardEstateSale;
    selectedEstateSale: MiniInfocardEstateSale;

    constructor() {
        effect(() => {
            if (this.miniInfocardEstate() != null && this.selectedSale()) {
                this.mapActionsService.removeEstatePolygon();
                const miniInfocardEstate = this.miniInfocardEstate();
                this.mapActionsService.addEstatePolygon(miniInfocardEstate);

                if (this.selectedSale().flyTo) {
                    if (miniInfocardEstate != null) {
                        this.targetEstateSale = miniInfocardEstate;
                        this.mapActionsService.addTargetEstateSaleMarker(miniInfocardEstate, () => this.setSelectedSale(this.targetEstateSale));
                        this.mapActionsService.flyTo(
                            {
                                lat: miniInfocardEstate.latitude,
                                lng: miniInfocardEstate.longitude,
                            },
                            15,
                        );
                    }
                }
            }
        });
    }

    setSelectedSale(selectedSale: MiniInfocardEstateSale): void {
        const saleParams: SaleInAreaParams = {
            estateId: selectedSale.estateId,
            unitId: selectedSale.unitId,
            estateType: EVEstateType[selectedSale.estateType],
            shareNumber: selectedSale.shareNumber,
            estateShareId: selectedSale.estateShareId,
            saleId: selectedSale.estateSaleId,
            orgNumber: selectedSale.organizationNumber ? selectedSale.organizationNumber.toString() : "",
        };
            this.selectedSale.set(saleParams);
    }

    setSale(sale: SaleInAreaParams): void {
        this.selectedSale.set(sale);
    }

    saleParams = computed(() => {
        const params = {
            ...this.marketSalesStateService.filterFormAPI(),
        };
        return params;
    });

    // Remove debouncedParams field, replace with combined signal
    private readonly debouncedCombined = toSignal(
        combineLatest([
            toObservable(this.saleParams),
            this.mapStateService.mapUpdateBoundsOrPolygons$.pipe(filter((v) => this.filterNoPolygonObs(v))),
            this.mapStateService.mapPolygonsChange$,
            this.mapStateService.mapMode$,
        ]).pipe(
            tap(() => {
                this.initHasRun = true;
            }),
            debounceTime(150),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        ),
    );

    saleResource = rxResource({
        params: () => this.debouncedCombined(),
        stream: ({ params }) => {
            if (!params || this.mapMode() !== MapMode.Market2) return of([] as any);
            const mapBoundState = this.mapStateService.getMapBoundsParams();
            const [filter, mapState, polygons] = params;
            if (mapState.state.zoom > this.minSalesZoom || this.areasStore.selectedAreas().length > 0 || polygons) {
                return this.http.post<IResponseWrapper<IResidenceSaleInArea>>(this.url, filter).pipe(map((res) => res.data));
            } else {
                return null;
            }
        },
    });

    salePositionsResource = rxResource({
        params: () => this.debouncedCombined(),
        stream: ({ params }) => {
            if (!params || this.mapMode() !== MapMode.Market2) return of([] as any);
            const [filter, mapState, polygons] = params;
            const mapBoundState = this.mapStateService.getMapBoundsParams();
            if (filter && mapState.state.zoom <= this.minSalesZoom && polygons == null) {
                var salePositionParams = {
                    ...filter,
                    latLongBounds: {
                        latMax: mapState.state.getMapBoundParams().maxLatitude,
                        latMin: mapState.state.getMapBoundParams().minLatitude,
                        longMax: mapState.state.getMapBoundParams().maxLongitude,
                        longMin: mapState.state.getMapBoundParams().minLongitude,
                    },
                };
                return this.http.post<IResponseWrapper<ISalePositionInArea[]>>(this.salePositionsurl, salePositionParams).pipe(map((res) => res.data));
            } else {
                return null;
            }
        },
    });

    estateSaleIsLoading = this.saleResource.isLoading;

    /**
     * True while either map data source is fetching. `saleResource` feeds the map above
     * `minSalesZoom`; below it the map is fed by `salePositionsResource` instead, so a
     * loader bound to `estateSaleIsLoading` alone would stay dead while panning zoomed out.
     */
    mapDataIsLoading = computed(() => this.saleResource.isLoading() || this.salePositionsResource.isLoading());

    miniInfocardEstate = computed(() => {
        if (this.miniInfocardEstateResource.hasValue() && this.miniInfocardEstateResource.value() != null) {
            if (this.targetEstateSale == null || this.targetEstateSale.unitId != this.miniInfocardEstateResource.value().unitId) {
                this.mapActionsService.addCurrentEstateSaleMarker(this.miniInfocardEstateResource.value());
            } else {
                this.mapActionsService.clearMarkerGroupLayer(MapMarkerType.SelectedSale);
                this.mapActionsService.clearMarkerGroupLayer(MapMarkerType.Outline);
            }
            this.selectedEstateSale = this.miniInfocardEstateResource.value();

            return this.miniInfocardEstateResource.value();
        }
        return null;
    });

    miniInfocardEstateResource = rxResource({
        params: () => this.selectedSale(),
        stream: ({ params }) => {
            if (!params) {
                this.mapActionsService.clearMarkerGroupLayer(MapMarkerType.SelectedSale);
                this.mapActionsService.clearMarkerGroupLayer(MapMarkerType.Outline);
                return of(null);
            }
            const sale = params;
            let onlyImagesForSaleId = null;
            let infoCardUrl = `${environment.apiUrl}/Map/EstateMiniInfocardData?`;
            const urlParams = [];

            if (sale.unitId === 0) {
                return of(null);
            }
            if (sale.unitId && sale.unitId != 0) {
                urlParams.push(`unitId=${sale.unitId}`);
            }

            if (sale.saleId) {
                urlParams.push(`saleId=${sale.saleId}`);
            }

            if (sale.housingCooperativeShareId) {
                urlParams.push(`hcShareId=${sale.housingCooperativeShareId}`);
            }

            if (sale.ownershipType === "Aksjeleilighet" || sale.ownershipType === "Obl.leilighet") {
                onlyImagesForSaleId = sale.saleId;
                urlParams.push(`onlyImagesForSaleId=${onlyImagesForSaleId}`);
                urlParams.push(`estateShareId=${sale.estateShareId}`);
            }

            infoCardUrl += urlParams.join("&");

            return this.http.get<IResponseWrapper<MiniInfocardEstateSale>>(infoCardUrl).pipe(map((res) => res.data));
        },
    });

    // miniInfocardEstateResource = this.estateDataService.getMiniInfocardEstateResource(this.selectedSale);

    private filterNoPolygonObs(value: IMapUpdate): boolean {
        const hasNoPolygon = value.polygons == null || value.polygons.length === 0;
        return hasNoPolygon || !this.initHasRun;
    }

    position = computed(() => {
        const keyInfo = this.miniInfocardEstateResource.value();
        if (keyInfo?.latitude && keyInfo?.longitude) {
            return {
                lat: keyInfo.latitude,
                lng: keyInfo.longitude,
            };
        }
        return null;
    });

    miniInfocardEstateImagePackages: Signal<ImagePackage[]> = computed(() => {
        let imagePackages: ImagePackage[] = [];
        if (this.miniInfocardEstateResource.hasValue() && this.miniInfocardEstateResource.value() != null) {
            imagePackages = [...imagePackages, ...this.miniInfocardEstateResource.value().imagePackages];
        }
        if (this.blomImagesResource.hasValue()) {
            imagePackages = [...imagePackages, ...this.blomImagesResource.value()];
        }
        return imagePackages;
    });

    private readonly blomImagesResource: ResourceRef<ImagePackage[]> = rxResource({
        params: () => this.position(),
        stream: ({ params: position }) => {
            if (position) {
                return this.resourceService.getBlomImagePackage({ lat: position.lat, lng: position.lng });
            }
            return of([]);
        },
    });
}

export interface IMapUpdate {
    state: MapBoundsState;
    polygons: ILatLng[][][];
}
